const yup = require('yup')
const { requiredFields, labelify } = require('./helpers')
const { uploadSchema } = require('./cloudinary-schema')
const mobileUserTypes = require('../mobile-user-types')
const { keys } = require('ramda')

const repeatOptions = ['none', 'daily', 'weekly', 'monthly', 'yearly', 'custom']

const audienceOptions = {
  rsvp: "RSVP'd",
  waitlist: 'waitlisted',
  rsvp_waitlist: `RSVP'd or waitlisted`,
}

const ledByOptions = {
  staff: 'Staff',
  resident: 'Resident',
  independent: 'Independent/self',
  volunteer: 'Volunteer',
}

const offsetMinutes = {
  5: '5 minutes before',
  15: '15 minutes before',
  30: '30 minutes before',
  60: '1 hour before',
  1440: '1 day before',
  4320: '3 days before',
  10080: '7 days before',
}

const linkedItemSchema = yup.object().shape({
  category: yup
    .string()
    .oneOf(['resource', 'reservation'])
    .when('$packages', (packages, schema) => {
      if (!packages) return schema
      if (packages['resources']) {
        return schema.default('resource')
      } else if (packages['reservations']) {
        return schema.default('reservation')
      }
    }),
  label: yup.string().default(''),
  resource: yup
    .mixed()
    .default('')
    .transform((curr, orig) => (orig === '' ? null : curr))
    .when(['category'], (category, schema) => {
      if (category !== 'resource') {
        return schema.transform(() => null)
      }
    }),
  reservationSchedule: yup
    .mixed()
    .default('')
    .transform((curr, orig) => (orig === '' ? null : curr))
    .when(['category'], (category, schema) => {
      if (category !== 'reservation') {
        return schema.transform(() => null)
      }
    }),
})

const automatedMessageSchema = yup.object().shape({
  title: yup.string().required().ensure().label('Message Title'),
  body: yup.string().required().ensure().label('Message Body'),
  audience: yup
    .string()
    .oneOf([...mobileUserTypes, ...keys(audienceOptions), 'all'])
    .default('all'),
  offsetMinutes: yup.number().oneOf(keys(offsetMinutes).map(Number)).required().default(5).label('Schedule Time'),
})

const shape = labelify({
  eventType: yup.string().oneOf(['Event', 'Menu']).default('Event'),
  featured: yup.boolean().default(false),
  title: yup.string().max(100).meta({ maxLength: 100 }).default(''),
  location: yup.string().max(100).meta({ maxLength: 100 }).default(''),
  resortEventLocation: yup.mixed().nullable(),
  locationLinkOnMobile: yup.boolean().default(false),
  pointOfInterest: yup.mixed().nullable(),
  label: yup.string().default(''),
  eventLabelRef: yup
    .mixed()
    .transform((v) => v || null)
    .nullable(),
  contentSource: yup.string().oneOf(['upload', 'create']).default('upload'),
  image: uploadSchema,
  editableLayout: yup.mixed(),
  libraryItem: yup.mixed(),
  startAt: yup.date().default(() => new Date()),
  endAt: yup
    .date()
    .min(yup.ref('startAt'))
    .default(() => new Date()),
  timezone: yup.string(),
  allDay: yup.boolean().default(false),
  multiDay: yup.boolean().default(false),
  intro: yup.string().max(200).meta({ maxLength: 200 }).default(''),
  details: yup.string().default(''),
  websiteDescription: yup.string().default(''),
  published: yup.boolean().nullable().default(false),
  cancelled: yup.boolean().nullable().default(false),
  restrictToUserType: yup.string().oneOf(mobileUserTypes.concat('')).ensure(),
  ledBy: yup.string().oneOf(keys(ledByOptions).concat('')).ensure(),
  repeatType: yup.string().oneOf(repeatOptions).default('none'),
  repeatRuleExceptDates: yup.array(yup.date()),
  calendars: yup.mixed().transform((value) => value || []),
  filterDevices: yup.string().oneOf(['all', 'selected', 'none']).default('none'),
  devices: yup.array().of(yup.string()).default([]),
  media: yup.array().of(yup.mixed()).default([]),
  mealLocation: yup
    .mixed()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr)),
  rrule: yup.object().shape({
    freq: yup.number().default(2),
    interval: yup.number().default(1),
    byweekday: yup.array().default([]),
    bysetpos: yup.number().default(0),
  }),
  rsvp: yup.object().shape({
    availability: yup.string().ensure().default('').label('RSVP availability'),
    capacity: yup.number().min(1).default(1).label('Maximum capacity'),
    maxPartySize: yup.number().min(1).max(yup.ref('capacity')).default(1).label('Maximum number in party'),
    isWaitlistEnabled: yup.boolean().nullable().default(false),
    availabilityWindow: yup.object().shape({
      opensDaysBeforeEvent: yup.number().min(0).default(0).label('Days before event when availability opens'),
      closesDaysBeforeEvent: yup.number().min(0).default(0).label('Days before event when availability closes'),
    }),
  }),
  linkedItems: yup.array().of(linkedItemSchema).default([]),
  automatedMessages: yup.array().of(automatedMessageSchema).default([]),
  editing: yup
    .object()
    .nullable()
    .shape({
      deleting: yup.boolean().nullable(),
      cancelling: yup.boolean().nullable(),
      confirm: yup.boolean().nullable(),
      range: yup.string().oneOf(['future', 'this', 'all']).default('all'),
      instance: yup
        .string()
        .nullable()
        .when('range', { is: 'future', then: (s) => s.required() }),
      targetInstance: yup.string().nullable(),
    }),
  updateDownloadableImage: yup.boolean().default(false),
  attendanceDisabled: yup.boolean().default(false),
})

const required = requiredFields(['eventType', 'title', 'startAt', 'endAt'])
const mealRequired = requiredFields(['eventType', 'title', 'startAt'])

const eventSchemaPartial = yup.object().shape(shape)
const eventSchema = eventSchemaPartial.shape(required(shape))
const eventFormSchema = eventSchema.shape({
  calendars: yup
    .array()
    .of(yup.string().ensure().label('Calendar'))
    .transform((value) => (!value || !value?.length ? [''] : value))
    .default(['']),
  startAt: yup.mixed().datetime(),
  endAt: yup.mixed().datetime(),
  scheduledMedia: yup.mixed(),
  eventLabelRef: yup.string().ensure(),
  repeatType: yup
    .string()
    .oneOf([...repeatOptions, 'weekdays'])
    .default('none'),
})

const mealSchema = eventSchemaPartial.shape(mealRequired(shape))
const mealFormSchema = mealSchema.shape({
  eventType: yup.string().oneOf(['Event', 'Menu']).default('Menu'),
  startAt: yup.mixed().datetime(),
  endAt: yup.mixed().datetime(),
  eventLabelRef: yup.string().ensure(),
  mealLocation: yup
    .mixed()
    .default('')
    .transform((curr, orig) => (orig === null ? '' : curr)),
})

const mealImportSchema = yup.object().shape({
  startAt: yup.mixed().datetime(),
  endAt: yup.mixed().datetime(),
  mealLocation: yup
    .mixed()
    .label('Location')
    .default('')
    .transform((curr, orig) => (orig === null ? '' : curr)),
  menu: yup.mixed().required().nullable().label('Menu'),
})

module.exports = {
  eventSchema,
  eventSchemaPartial,
  eventFormSchema,
  mealSchema,
  mealFormSchema,
  linkedItemSchema,
  mealImportSchema,
  automatedMessageSchema,
  offsetMinutes,
  ledByOptions,
}
